<template>
    <div class="flex flex-col w-full">
        <vs-table
            :id="`sst-${_uid}`"
            v-bind="$attrs"
            ref="table"
            sst
            :stripe="striped"
            class="server-side-table flex flex-col"
            :class="{ 'has-data': isLoadingActiveSet || data.any() }"
            v-model="localValue"
            :data="data"
            :max-items="pageSize"
            @search="onSearch"
            @sort="onSort"
            :noDataText="noDataText"
            :hover-flat="disableSelection"
        >
            <!-- pagination -->
            <template slot="header">
                <vs-select
                    v-if="!fixedPageSize"
                    class="selectExample"
                    label="Page Size"
                    v-model="pageSize"
                    autocomplete
                >
                    <vs-select-item
                        v-for="(item, index) in pageSizeOptions"
                        :key="index"
                        :value="item.value"
                        :text="item.text"
                    />
                </vs-select>
                <slot name="header-inner"></slot>
                <slot name="header-filter"></slot>
            </template>

            <template slot="thead">
                <template v-for="(column, colIndex) in columns">
                    <vs-th
                        v-if="column.visible == undefined || column.visible"
                        :key="'head-' + colIndex"
                        :sort-key="column.sort"
                        :class="column.headClass"
                    >
                        <slot :name="`${column.title}_head`" :column="column" :col-index="colIndex">
                            <span :class="column.headerClass" class="w-full whitespace-nowrap">
                                {{ column.title }}
                            </span>
                        </slot>
                    </vs-th>
                </template>
            </template>

            <template slot-scope="{ data }">
                <tbody class="vx-table-body" ref="tbody">
                    <transition
                        v-for="(row, index) in data"
                        :key="index"
                        name="list-enter-up"
                        appear
                    >
                        <vs-tr
                            v-if="!disabled"
                            class="table-row"
                            :key="getKey(row, index)"
                            :data="row"
                            :class="getRowClasses(row)"
                            :ref="row.xIndex"
                            :style="{
                                cursor: disableSelection ? 'default' : 'cursor',
                                transitionDelay: index * 0.02 + 's'
                            }"
                        >
                            <template v-for="(column, colIndex) in columns">
                                <vs-td
                                    v-if="getVisibility(row, column)"
                                    :key="'col-' + colIndex"
                                    :style="column.style"
                                    :class="getClassesX(row, column)"
                                >
                                    <slot
                                        :name="column.slotName || column.title.replace(/\s/g, '')"
                                        :row="row"
                                        :col-index="colIndex"
                                        :row-index="index"
                                    >
                                        <span>
                                            {{ getValueX(row, column) }}
                                        </span>
                                    </slot>
                                </vs-td>
                            </template>
                        </vs-tr>
                    </transition>
                </tbody>
            </template>
        </vs-table>

        <template v-if="disabled">
            <slot name="disabled">
                <div class="w-full flex justify-center items-center text-warning">
                    <vx-basic-icon color="warning" icon="AlertTriangleIcon" size="8" />
                    <span class="mx-3 my-4 text-warning text-center"> This table is disabled </span>
                    <vx-basic-icon color="warning" icon="AlertTriangleIcon" size="8" />
                </div>
            </slot>
        </template>

        <vx-pagination class="mt-4" :total="totalPages" v-model="current_page"></vx-pagination>
        <div v-if="showTotals" class="totals">
            <span
                >{{ startingNumberOfList }} -
                {{
                    lastPageNumber == current_page
                        ? NumberOfListInlastPage
                        : current_page * pageSize
                }}
                of {{ totalCountLabel }} Records
            </span>
        </div>
    </div>
</template>

<script>
import Vue from 'vue'
import { mapGetters } from 'vuex'

/*
example column object:
    {
        title: 'Customer',                              // The heading title
        sort: 'account.name',                           // The sort id which is passed to the backend
        value: 'customer',                              // A way to access the display value for the cell, can be a function or key (use . to parse an object tree ie. bdm.name)
        visible: true,                                  // Reactively show/hide the cell (defaults to true)
        style: 'width: 50%; color: green;'              // Styles to apply to the cell
        class: 'my-class flex flex-col'                 // Classes to apply to the cell
        format: (row) => row.date.toHyphenatedDateString()     // Function which receives the value and further formats it
        slotName: 'FullName'                            // Optional slot name when title has spaces (or is different). Defaults to the title if not supplied
    },
*/
export default {
    name: 'vx-server-side-table',
    props: {
        value: {
            type: Object
        },
        fetchData: {
            type: Function,
            required: true
        },
        idKey: {
            type: [Function, String],
            default: 'id'
        },
        columns: {
            type: Array,
            required: true
        },
        striped: {
            type: Boolean,
            default: true
        },
        filters: {
            type: Object,
            default: () => {}
        },
        rowClass: {
            type: [Function, String],
            default: ''
        },
        showTotals: {
            type: Boolean,
            default: true
        },
        fixedPageSize: {
            type: Number,
            required: false
        },
        disabled: {
            type: Boolean,
            default: false
        },
        disableSelection: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            onActiveRoute: true,
            searchTimeout: null,
            currentPage: 1,
            searchText: null,
            sortKey: null,
            sortType: null,
            pageSize: 15,
            typingDelay: 1000,
            pageSizeOptions: [
                { text: '10', value: 10 },
                { text: '15', value: 15 },
                { text: '50', value: 50 },
                { text: '100', value: 100 },
                { text: '200', value: 200 }
            ],
            cachedData: {
                initial: {
                    error: false,
                    loading: true,
                    pages: 1,
                    count: 0,
                    data: []
                }
            },
            parameterHash: 'initial'
        }
    },
    watch: {
        parameters: {
            handler: function (val, oldV) {
                this.getData(val, oldV)
            },
            immediate: true,
            deep: true
        },
        pageResetParameters: {
            handler: function (val, oldV) {
                this.currentPage = 1
            },
            deep: true
        },
        isLoadingActiveSet: {
            handler: function (val) {
                this.setTableDataLoadSpinner(val)
            },
            immediate: true
        },
        isActive(newV, oldV) {
            if (newV === true) {
                this.getData(this.parameters)
            }
        },
        data: {
            handler: function (newV) {
                this.$emit('page-results-changed', newV)
            },
            deep: true
        }
    },
    computed: {
        localValue: {
            get() {
                return this.value
            },
            set(val) {
                this.$emit('input', val)
            }
        },
        noDataText() {
            return this.disabled ? '' : 'No Data Available'
        },
        isActive() {
            return this.onActiveRoute && !this.disabled
        },
        pageSize_x() {
            return this.fixedPageSize || this.pageSize
        },
        activeDataSet() {
            return this.cachedData[this.parameterHash]
        },
        totalCountLabel() {
            return this.cachedData[this.parameterHash].count
        },
        getKey() {
            if (typeof this.idKey === 'function') {
                return this.idKey
            }
            return (row, index) => {
                return row[this.idKey] || index
            }
        },
        current_page: {
            get() {
                return this.currentPage
            },
            set(val) {
                this.currentPage = val
            }
        },
        data() {
            return this.activeDataSet.data
        },
        totalPages() {
            return this.activeDataSet.pages || 1
        },
        localFilters() {
            return {
                //search: this.searchText,
                ...this.filters
            }
        },
        pageResetParameters() {
            return {
                ...this.localFilters,
                sortKey: this.sortKey,
                pageSize: this.pageSize_x
            }
        },
        parameters() {
            return {
                ...this.pageResetParameters,
                // sortKey: this.sortKey || this.pageResetParameters.sortKey,
                sortType: this.sortType || this.pageResetParameters.sortType,
                pageNo: this.currentPage - 1 // 0 indexed on server side
            }
        },
        isLoadingActiveSet() {
            return !this.disabled && this.activeDataSet.loading
        },
        getRowClasses() {
            if (!this.rowClass) {
                return () => ''
            } else if (typeof this.rowClass === 'function') {
                return this.rowClass
            }
            return () => this.rowClass
        },
        lastPageNumber() {
            return Math.ceil(this.totalCountLabel / this.pageSize)
        },
        startingNumberOfList() {
            return (this.current_page - 1) * this.pageSize + 1
        },

        NumberOfListInlastPage() {
            let numberOfRest = this.totalCountLabel % this.pageSize
            // It has to minus 1, because startingNumberOfList is counted as well.
            return this.startingNumberOfList - 1 + numberOfRest
        }
    },
    methods: {
        setTableDataLoadSpinner(show) {
            // if not done in nextTick, this selects a dom element before it has been added to the dom
            // this may be nothing or it may be the prior route.
            this.$nextTick(() => {
                // Using the _uid to guarantee the table selected by this is a child of this table and not another
                var el = document.querySelector(`#sst-${this._uid} .vs-con-tbody`)
                if (el) {
                    if (show) {
                        this.$vs.loading({ container: el })
                    } else {
                        this.$vs.loading.close(el)
                    }
                }
            })
        },
        reset() {
            this.parameterHash = 'initial'
            this.cachedData = {
                initial: {
                    error: false,
                    loading: true,
                    pages: 1,
                    count: 0,
                    data: []
                }
            }
            this.getData(this.parameters)
        },
        getClassesX(row, column) {
            if (!column.class) {
                return ''
            } else if (typeof column.class === 'string') {
                return column.class
            }

            return column.class(row)
        },
        getRowDisabled() {},
        getVisibility(row, column) {
            if (column.visible == undefined) {
                return true
            } else if (typeof column.visible === 'boolean') {
                return column.visible
            }

            return column.visible(row)
        },

        getValueX(row, column) {
            var val = null
            if (!column.value) {
                val = row
            } else if (typeof column.value === 'string') {
                val = this.getValue(row, column.value)
            } else {
                val = column.value(row)
            }

            if (column.format) {
                return column.format(val)
            }
            return val
        },
        updateRecord(record) {
            var recordId = this.getKey(record)
            // iterate through each set of cached search data
            // merge the new record into any which share the updated record's id
            Object.keys(this.cachedData).forEach((cacheKey) => {
                this.cachedData[cacheKey].data.forEach((cachedRecord) => {
                    if (this.getKey(cachedRecord) === recordId) {
                        Object.assign(cachedRecord, record)
                    }
                })
            })
        },
        generateHash(parameters) {
            return Object.values(parameters || {}).join('_')
        },
        onSearch(searchText) {
            if (this.searchTimeout != null) {
                clearTimeout(this.searchTimeout)
                this.searchTimeout = null
            }

            this.searchTimeout = setTimeout(
                function () {
                    this.searchTimeout = null
                    // If a search handler is bound, allow it to handle
                    // the filter. This allows multiple components to be bound
                    // to the same filter set at a higher level (eg: summaries)
                    if (this.$listeners.search) {
                        if (this.isActive) {
                            this.$emit('search', searchText)
                        }
                    } else {
                        this.searchText = searchText || ''
                    }
                    this.$emit('update:filters', { ...this.filters, search: searchText })
                }.bind(this),
                this.typingDelay
            )
        },
        onSort(sortKey, sortType) {
            // if no sort type is passed, removed the sort key as it is no longer sorted on
            // this way it will y=use the original cached data set
            this.sortKey = sortType ? sortKey : null
            this.sortType = sortType
        },
        getValue(row, column) {
            if (column.includes('.')) {
                let split = column.split('.')

                let finalVal = row
                for (let i = 0; i < split.length; i++) {
                    if (finalVal) {
                        finalVal = finalVal[split[i]] || ''
                    }
                }
                return finalVal
            } else {
                if (row) {
                    return row[column]
                } else {
                    return ''
                }
            }
        },
        retrieveCache(hash) {
            return this.cachedData[hash]
        },
        async getData(params) {
            if (!this.isActive) {
                return
            }

            var hash = this.generateHash(params)
            if (this.retrieveCache(hash)) {
                this.parameterHash = hash
                return
            }

            Vue.set(this.cachedData, hash, {
                error: false,
                loading: true,
                count: this.activeDataSet.count,
                pages: this.activeDataSet.pages,
                data: this.setIndexX(this.activeDataSet.data)
            })

            this.parameterHash = hash

            try {
                var response = this.fetchData(params)

                // check if function was async
                if (response.then) {
                    response = await response
                }

                Vue.set(this.cachedData, hash, {
                    error: false,
                    count: response.totalCount,
                    pages: response.totalPages,
                    data: this.setIndexX(response.data)
                })
            } catch (err) {
                this.cachedData[hash].error = err
                this.notifyError(
                    'Communication Error',
                    'Could not retrieve records, please check your connection and try again.',
                    3000
                )
            } finally {
                this.cachedData[hash].loading = false
            }
        },
        //Test : Usecase to add xindex on each row alike with client-side table functionality for linedetail purpose.
        setIndexX(data) {
            if (data.length === 0) {
                return []
            }
            return data.map(function (value, index) {
                return { ...value, xIndex: index + 1 }
            })
        }
    },
    activated() {
        // isActive is defaulted to true, so this will only run when the component has been kept alive
        if (!this.onActiveRoute) {
            this.onActiveRoute = true
        }
    },
    deactivated() {
        this.onActiveRoute = false
    }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/vuexy/_variables.scss';

.totals {
    position: absolute;
    bottom: 2.5rem;
    left: 2.5rem;
}

.server-side-table {
    ::v-deep table {
        background-color: transparent;
        background: none;
    }
    ::v-deep .vs-table--header {
        align-items: end;
        margin-bottom: 1rem;
    }

    ::v-deep .vs-table--search {
        padding: 0 !important;
    }

    height: calc(100% - 54px);

    ::v-deep .vs-pagination--li {
        font-size: 12px;
    }

    .vx-table-body {
        height: 100%;
        overflow: hidden;
    }

    ::v-deep .vs-table--content {
        flex-grow: 1;
        overflow: hidden !important;
    }

    ::v-deep thead,
    ::v-deep th {
        background-color: inherit;
        position: sticky;
        top: 0;
        z-index: 1;

        .vs-table-text {
            color: rgba(var(--vs-primary), 1) !important;
        }
    }

    ::v-deep .vs-table--tbody {
        overflow: auto;
    }

    &.has-data {
        ::v-deep .vs-table--tbody {
            height: 100%;
            min-height: 200px;
        }
    }
}
</style>
